import { useEffect } from "react";
import brand from "../brand.config";
import { applyFontSource } from "../fonts";

const GlobalStyle = () => {
  useEffect(() => { applyFontSource(brand.fonts.importUrl); }, []);
  return (
  <style>{`
    *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

    :root {
      --bg: ${brand.colors.bg};
      --surface: ${brand.colors.surface};
      --surface2: ${brand.colors.surface2};
      --border: ${brand.colors.border};
      --amber: ${brand.colors.accent};
      --amber-dim: ${brand.colors.accentDim};
      --text: ${brand.colors.text};
      --text-dim: ${brand.colors.textDim};
      --text-muted: ${brand.colors.textMuted};
      --red: ${brand.colors.red};
      --green: ${brand.colors.green};
      --radius: ${brand.colors.radius};
      --font-display: ${brand.fonts.display};
      --font-mono: ${brand.fonts.mono};
      --font-body: ${brand.fonts.body};

      --shadow-sm: 0 2px 8px rgba(0,0,0,0.2);
      --shadow-md: 0 8px 24px rgba(0,0,0,0.3);
      --shadow-lg: 0 20px 48px rgba(0,0,0,0.4);
      --shadow-glow: 0 0 20px rgba(0,212,170,0.15);
      --ease-spring: cubic-bezier(0.34, 1.56, 0.64, 1);
      --ease-out: cubic-bezier(0.16, 1, 0.3, 1);
    }

    html { overflow-x: hidden; width: 100%; scroll-behavior: smooth; }
    html { height: 100%; background: var(--bg); }
    body { background: var(--bg); color: var(--text); font-family: var(--font-body); font-size: 15px; line-height: 1.5; min-height: 100%; overflow-x: hidden; width: 100%; position: relative; padding-bottom: env(safe-area-inset-bottom); }
    #root { min-height: 100vh; min-height: 100dvh; }

    h1, h2, h3 { font-family: var(--font-display); font-weight: 400; }

    button { cursor: pointer; border: none; background: none; font-family: var(--font-body); }

    input, select, textarea {
      background: var(--surface2);
      border: 1px solid var(--border);
      color: var(--text);
      font-family: var(--font-body);
      font-size: 14px;
      padding: 10px 12px;
      border-radius: var(--radius);
      width: 100%;
      box-sizing: border-box;
      outline: none;
      transition: border-color 0.2s, box-shadow 0.2s;
    }
    input[type="date"] { -webkit-appearance: none; -moz-appearance: none; appearance: none; max-width: 100%; min-height: 42px; text-align: left; }
    input:focus, select:focus, textarea:focus {
      border-color: var(--amber);
      box-shadow: 0 0 0 3px rgba(0,212,170,0.1);
    }
    input::placeholder, textarea::placeholder { color: var(--text-muted); }

    a { color: inherit; text-decoration: none; }

    /* Custom checkboxes */
    input[type="checkbox"] {
      -webkit-appearance: none;
      -moz-appearance: none;
      appearance: none;
      width: 18px;
      height: 18px;
      border: 2px solid var(--border);
      border-radius: 4px;
      background: var(--surface2);
      cursor: pointer;
      position: relative;
      flex-shrink: 0;
      transition: all 0.15s ease;
    }
    input[type="checkbox"]:checked {
      background: var(--amber);
      border-color: var(--amber);
    }
    input[type="checkbox"]:checked::after {
      content: "✓";
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      color: #111;
      font-size: 12px;
      font-weight: 700;
    }
    input[type="checkbox"]:hover {
      border-color: var(--amber-dim);
    }

    /* react-phone-number-input dark theme overrides */
    .PhoneInput { display: flex; gap: 8px; align-items: center; }
    .PhoneInputCountry { display: flex; align-items: center; gap: 6px; padding: 0 10px; background: var(--surface2); border: 1px solid var(--border); border-radius: var(--radius); height: 40px; flex-shrink: 0; }
    .PhoneInputCountrySelect { background: transparent; border: none; color: var(--text); font-size: 14px; outline: none; cursor: pointer; }
    .PhoneInputCountrySelect option { background: var(--surface2); color: var(--text); }
    .PhoneInputCountrySelectArrow { border-color: var(--text-dim); }
    .PhoneInputInput { flex: 1; background: var(--surface2); border: 1px solid var(--border); color: var(--text); font-family: var(--font-body); font-size: 14px; padding: 10px 12px; border-radius: var(--radius); outline: none; transition: border-color 0.2s, box-shadow 0.2s; }
    .PhoneInputInput:focus { border-color: var(--amber); box-shadow: 0 0 0 3px rgba(0,212,170,0.1); }
    .PhoneInputInput::placeholder { color: var(--text-muted); }

    ::-webkit-scrollbar { width: 6px; }
    ::-webkit-scrollbar-track { background: var(--bg); }
    ::-webkit-scrollbar-thumb { background: var(--border); border-radius: 3px; }
    ::-webkit-scrollbar-thumb:hover { background: var(--text-muted); }

    /* Google Places autocomplete dropdown above modals */
    .pac-container { z-index: 10000 !important; }
    gmp-place-autocomplete { z-index: 10000; position: relative; }

    /* ── Animations ── */
    @keyframes fadeUp {
      from { opacity: 0; transform: translateY(20px); }
      to { opacity: 1; transform: translateY(0); }
    }
    @keyframes fadeIn {
      from { opacity: 0; }
      to { opacity: 1; }
    }
    @keyframes slideIn {
      from { opacity: 0; transform: translateX(20px); }
      to { opacity: 1; transform: translateX(0); }
    }
    @keyframes scaleIn {
      from { opacity: 0; transform: scale(0.95); }
      to { opacity: 1; transform: scale(1); }
    }
    @keyframes pulse {
      0%, 100% { opacity: 1; }
      50% { opacity: 0.5; }
    }
    @keyframes shimmer {
      0% { background-position: -200% 0; }
      100% { background-position: 200% 0; }
    }
    @keyframes cartConfirmFade {
      0% { opacity: 0; transform: translateY(4px) scale(0.9); }
      20% { opacity: 1; transform: translateY(0) scale(1); }
      70% { opacity: 1; transform: translateY(0) scale(1); }
      100% { opacity: 0; transform: translateY(-6px) scale(0.95); }
    }
    @keyframes cartBounce {
      0% { transform: scale(1); }
      30% { transform: scale(1.15); }
      50% { transform: scale(0.95); }
      70% { transform: scale(1.05); }
      100% { transform: scale(1); }
    }
    @keyframes glowPulse {
      0%, 100% { box-shadow: 0 0 12px rgba(0,212,170,0.2); }
      50% { box-shadow: 0 0 24px rgba(0,212,170,0.35); }
    }

    .fade-up { animation: fadeUp 0.5s var(--ease-out) forwards; }
    .fade-up-2 { animation: fadeUp 0.5s 0.08s var(--ease-out) both; }
    .fade-up-3 { animation: fadeUp 0.5s 0.16s var(--ease-out) both; }
    .fade-up-4 { animation: fadeUp 0.5s 0.24s var(--ease-out) both; }
    .fade-in { animation: fadeIn 0.4s ease both; }
    .scale-in { animation: scaleIn 0.3s var(--ease-spring) both; }

    /* Product card styles */
    .product-card {
      background: var(--surface);
      border: 1px solid var(--border);
      border-radius: 6px;
      overflow: hidden;
      cursor: pointer;
      transition: transform 0.3s var(--ease-out), box-shadow 0.3s var(--ease-out), border-color 0.3s;
    }
    .product-card:hover {
      transform: translateY(-4px);
      box-shadow: var(--shadow-md);
      border-color: var(--amber);
    }
    .product-card:active {
      transform: translateY(-2px) scale(0.99);
    }
    .product-card .card-img {
      overflow: hidden;
    }
    .product-card .card-img img {
      transition: transform 0.5s var(--ease-out);
    }
    .product-card:hover .card-img img {
      transform: scale(1.08);
    }

    /* Interactive button base */
    .btn-interactive {
      transition: all 0.2s var(--ease-out);
    }
    .btn-interactive:hover {
      transform: translateY(-1px);
      box-shadow: var(--shadow-sm);
    }
    .btn-interactive:active {
      transform: translateY(0) scale(0.97);
      box-shadow: none;
    }

    /* Skeleton loader */
    .skeleton {
      background: linear-gradient(90deg, var(--surface2) 0%, var(--border) 50%, var(--surface2) 100%);
      background-size: 200% 100%;
      animation: shimmer 1.5s ease-in-out infinite;
      border-radius: var(--radius);
    }

    /* ── Mobile responsive ── */
    @media (max-width: 768px) {
      html, body, #root { max-width: 100% !important; overflow-x: hidden !important; width: 100% !important; }
      img { max-width: 100% !important; height: auto !important; }
      input, select, textarea { max-width: 100% !important; }
      table { display: block; overflow-x: auto; -webkit-overflow-scrolling: touch; }
      pre, code { word-break: break-all; }

      .nav-links { display: none !important; }
      .nav-links.open { display: flex !important; flex-direction: column; position: absolute; top: 60px; left: 0; right: 0; background: var(--surface); border-bottom: 1px solid var(--border); padding: 12px 24px; gap: 4px !important; z-index: 99; box-shadow: var(--shadow-lg); animation: fadeUp 0.2s var(--ease-out) both; }
      .nav-links.open > * { width: 100% !important; box-sizing: border-box; text-align: center; justify-content: center; }
      .mobile-menu-btn { display: flex !important; }

      .r-grid { grid-template-columns: 1fr !important; }
      .r-tabs-desktop { display: none !important; }
      .r-tabs-mobile { display: block !important; }
      .r-gap { gap: 24px !important; }

      .product-info-col { order: -1 !important; }
      .product-img-container { height: 300px !important; }

      .r-scroll { overflow-x: auto !important; -webkit-overflow-scrolling: touch; flex-wrap: nowrap !important; }
      .r-scroll > button { flex-shrink: 0 !important; }
    }
    @media (min-width: 769px) {
      .mobile-menu-btn { display: none !important; }
    }

    /* Reduced motion */
    @media (prefers-reduced-motion: reduce) {
      *, *::before, *::after { animation-duration: 0.01ms !important; transition-duration: 0.01ms !important; }
    }
  `}</style>
  );
};

export default GlobalStyle;
